import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import INFO from "../data/user";
import "./styles/cv.css";

const CV = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const experience = [
		{
			company: "XeoHealth Inc.",
			role: "Software Engineer",
			period: "2024 - Present",
			description: "Lead full-stack development initiatives for healthcare technology solutions. Architect and implement scalable web applications using modern frameworks and best practices.",
			highlights: ["Angular", "PrimeNG", "Healthcare Tech", "Full-Stack Development", "Figma"]
		},
		{
			company: "Allstate Insurance",
			role: "Front-End Developer",
			period: "2020 - 2023",
			description: "Developed and maintained customer-facing insurance platforms and dealer services applications. Led frontend architecture decisions and mentored junior developers.",
			highlights: ["React", "JavaScript ES6+", "API Integration", "Team Leadership"]
		},
		{
			company: "Allstate Insurance",
			role: "Web Developer",
			period: "2017 - 2020",
			description: "Built responsive web applications and enhanced user experiences across multiple insurance products. Collaborated with cross-functional teams to deliver high-quality solutions.",
			highlights: ["JavaScript", "HTML5/CSS3", "Responsive Design", "Cross-browser Compatibility"]
		},
		{
			company: "Allstate Insurance",
			role: "UX/UI Designer",
			period: "2014 - 2017",
			description: "Designed user interfaces and experiences for insurance applications. Conducted user research and created wireframes, prototypes, and design systems.",
			highlights: ["UI/UX Design", "Prototyping", "User Research", "Design Systems", "Adobe XD"]
		},
		{
			company: "Allstate Insurance",
			role: "Web Designer",
			period: "2010 - 2014",
			description: "Created visual designs for web applications and marketing materials. Developed brand consistency across digital platforms and collaborated with development teams.",
			highlights: ["Web Design", "Visual Design", "Brand Development", "HTML/CSS"]
		},
		{
			company: "Madden Media",
			role: "Web Designer",
			period: "2006 - 2010",
			description: "Designed and developed websites for diverse clients across multiple industries. Created engaging digital experiences while maintaining brand identity and usability standards.",
			highlights: ["Web Design", "Client Management", "Cross-industry Experience", "Creative Problem Solving"]
		},
		{
			company: "Logistic Services Intl.",
			role: "Graphic Specialist",
			period: "2004 - 2006",
			description: "Created graphic designs and visual communications for logistics and supply chain documentation. Developed marketing materials and technical illustrations.",
			highlights: ["Graphic Design", "Technical Illustration", "Print Design", "Adobe Creative Suite"]
		}
	];

	const skills = {
		"Libraries": ["Angular", "React", "Redux", "PrimeNG", "Bootstrap"],
		"Languages": ["HTML5", "CSS3", "JavaScript ES6+", "TypeScript"],
		"Design": [ "Figma", "Responsive Design", "UI/UX"]
	};

	const projects = [
		{
			name: "Extended Vehicle Care",
			description: "Enterprise warranty platform with React frontend and Spring Boot backend",
			tech: ["React", "Java", "Spring Boot", "SQL Server"],
			link: "https://vehicleprotection.allstate.com/"
		},
		{
			name: "Allstate Dealer Services",
			description: "Marketing website built with .NET MVC and modern JavaScript",
			tech: ["React", "JavaScript", "Bootstrap", ".NET", "SQL Server"],
			link: "https://www.allstatedealerservices.com/"
		}
	];

	return (
		<React.Fragment>
			<Helmet>
				<title>{INFO.main.title}</title>
				<meta name="description" content="Ryan Carr - Full Stack Engineer | AI-Accelerated Development CV" />
			</Helmet>

			<div className="cv-container">
				<header className="cv-header">
					<div className="cv-header-content">
						<div className="cv-header-left">
							<h1 className="cv-name">{INFO.main.name}</h1>
							<h2 className="cv-title">Full Stack Engineer | AI-Accelerated Development</h2>
							<p className="cv-tagline">
								Building and shipping production apps end to end — front end to cloud to AI
							</p>
						</div>
						<div className="cv-header-right">
							<img
								src="/headshot_2026_web.jpg"
								alt="Ryan Carr"
								className="cv-photo"
							/>
						</div>
					</div>
					<div className="cv-contact">
						<a href={`mailto:${INFO.main.email}`} className="cv-contact-item">
							<FontAwesomeIcon icon={faEnvelope} />
							<span>{INFO.main.email}</span>
						</a>
						<a href={INFO.socials.github} target="_blank" rel="noreferrer" className="cv-contact-item">
							<FontAwesomeIcon icon={faGithub} />
							<span>GitHub</span>
						</a>
						<a href={INFO.socials.linkedin} target="_blank" rel="noreferrer" className="cv-contact-item">
							<FontAwesomeIcon icon={faLinkedin} />
							<span>LinkedIn</span>
						</a>
					</div>
				</header>

				<main className="cv-main">
					<section className="cv-section">
						<h3 className="cv-section-title">About</h3>
						<p className="cv-about">
							I build and ship production applications end to end. My work spans React and Angular front ends, serverless backends on Firebase and Google Cloud, and AI-driven features powered by the Claude API. I leverage AI-accelerated development workflows daily through tools like Claude Code and GitHub Copilot, delivering at speed without cutting corners on quality. I'm experienced in designing, building, and deploying enterprise-grade, public-facing applications with clean architecture and a focus on real user impact.
						</p>
					</section>

					<section className="cv-section">
						<h3 className="cv-section-title">Experience</h3>
						<div className="cv-experience">
							{experience.map((job, index) => (
								<div key={index} className="cv-job">
									<div className="cv-job-header">
										<div>
											<h4 className="cv-job-title">{job.role}</h4>
											<h5 className="cv-job-company">{job.company}</h5>
										</div>
										<span className="cv-job-period">{job.period}</span>
									</div>
									<p className="cv-job-description">{job.description}</p>
									<div className="cv-job-highlights">
										{job.highlights.map((skill, idx) => (
											<span key={idx} className="cv-tag">{skill}</span>
										))}
									</div>
								</div>
							))}
						</div>
					</section>

					<section className="cv-section">
						<h3 className="cv-section-title">Skills</h3>
						<div className="cv-skills">
							{Object.entries(skills).map(([category, items]) => (
								<div key={category} className="cv-skill-category">
									<h4 className="cv-skill-category-title">{category}</h4>
									<div className="cv-skill-list">
										{items.map((skill, index) => (
											<span key={index} className="cv-skill-item">{skill}</span>
										))}
									</div>
								</div>
							))}
						</div>
					</section>

					<section className="cv-section">
						<h3 className="cv-section-title">Featured Projects</h3>
						<div className="cv-projects">
							{projects.map((project, index) => (
								<div key={index} className="cv-project">
									<div className="cv-project-header">
										<h4 className="cv-project-name">{project.name}</h4>
										<a 
											href={project.link} 
											target="_blank" 
											rel="noreferrer" 
											className="cv-project-link"
										>
											<FontAwesomeIcon icon={faExternalLinkAlt} />
										</a>
									</div>
									<p className="cv-project-description">{project.description}</p>
									<div className="cv-project-tech">
										{project.tech.map((tech, idx) => (
											<span key={idx} className="cv-tech-tag">{tech}</span>
										))}
									</div>
								</div>
							))}
						</div>
					</section>
				</main>

				<footer className="cv-footer">
					<p>&copy; {new Date().getFullYear()} {INFO.main.name}. All rights reserved.</p>
				</footer>
			</div>
		</React.Fragment>
	);
};

export default CV;